import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "../../components/misc/Button";
import { Card, CardContent } from "../../components/misc/Card";
import CustomToast from "../../components/misc/CustomToast";
import { Input } from "../../components/misc/Input";
import { Tabs, TabsList, TabsTrigger } from "../../components/misc/Tabs";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import AccountHealthList from "./AccountHealthList";
import AccountHealthMetricCards from "./AccountHealthMetricCards";
import { getAccountHealth } from "./actions";
import {
    AccountHealthFilter,
    AccountHealthOverview,
    FILTERS,
    FILTER_LABELS,
    formatEastern,
    inBucket,
} from "./types";

const AccountHealth: React.FC = () => {
    const isMobile = useIsMobile(900);
    const [searchParams] = useSearchParams();
    const day = searchParams.get("day") ?? undefined;

    const [data, setData] = useState<AccountHealthOverview | null>(null);
    const [loading, setLoading] = useState(true);
    // Why the last load failed (the server's own words), or null. A failed
    // load must never pass for an empty list.
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [filter, setFilter] = useState<AccountHealthFilter>("all");
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setData(null);
        setError(null);
        setExpanded(new Set());
        getAccountHealth(day)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setData(null);
                setError(err instanceof Error ? err.message : "The server did not answer.");
                toast.error(<CustomToast message="Could not load account health" type="error" />, {
                    autoClose: 3000,
                });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [day, attempt]);

    const rows = useMemo(() => data?.rows ?? [], [data]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(
            (r) =>
                inBucket(r, filter) &&
                (!q || [r.name, r.email, r.accountName ?? ""].some((s) => s.toLowerCase().includes(q))),
        );
    }, [rows, filter, search]);

    const countFor = (f: AccountHealthFilter): number => rows.filter((r) => inBucket(r, f)).length;

    const toggle = (userId: string): void =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });

    const emptyMessage = !data
        ? ""
        : data.day === null
          ? "No snapshot yet - the nightly check hasn't run."
          : data.computedAt === null
            ? `No snapshot for the night of ${data.day}.`
            : "No accounts in this view.";

    return (
        <div className={cn("flex flex-col gap-4", isMobile ? "p-4" : "p-6")}>
            <div className={cn("flex gap-4", isMobile ? "flex-col items-start" : "flex-row items-start justify-between")}>
                <div>
                    <h1 className="text-xl font-bold text-primary">Account health</h1>
                    <p className="text-xs text-gray-500">
                        Every paying account, scored nightly. The same list the Monday digest sends, with the
                        same reasons; a channel the customer removed themselves does not count against them.
                    </p>
                </div>

                <div className={cn("flex flex-col gap-1 text-xs", isMobile ? "items-start" : "items-end")}>
                    <div className="text-gray-500">
                        Last check: {data?.computedAt ? formatEastern(data.computedAt) : "-"}
                        {data?.day && day ? ` (night of ${data.day})` : ""}
                    </div>
                    <div className="flex items-center gap-2">
                        {data && (
                            <span
                                className={cn(
                                    "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                                    data.alertsEnabled ? "bg-[#beeb9f] text-primary" : "bg-gray-200 text-gray-700",
                                )}
                            >
                                {data.alertsEnabled ? "Alert emails on" : "Shadow mode - alert emails off"}
                            </span>
                        )}
                        <Link to="/admin/app-config" className="text-primary underline">
                            Thresholds
                        </Link>
                    </div>
                </div>
            </div>

            {data && (
                <AccountHealthMetricCards
                    counts={data.counts}
                    active={filter}
                    onSelect={(f) => setFilter((current) => (current === f ? "all" : f))}
                />
            )}

            {!!data?.waitingForWriter && (
                <p className="text-sm text-gray-600">
                    {data.waitingForWriter} post{data.waitingForWriter > 1 ? "s" : ""} waiting for the
                    writer.{" "}
                    <Link to="/admin/failed-posts" className="text-primary underline">
                        See Failed Posts
                    </Link>
                </p>
            )}

            <div className={cn("flex gap-3", isMobile ? "flex-col" : "flex-row items-center justify-between")}>
                <Tabs value={filter} onValueChange={(v) => setFilter(v as AccountHealthFilter)}>
                    <TabsList className="w-fit">
                        {FILTERS.map((f) => (
                            <TabsTrigger key={f} value={f}>
                                {FILTER_LABELS[f]}
                                {data ? ` (${countFor(f)})` : ""}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, email or account"
                    className={cn(isMobile ? "w-full" : "max-w-[320px]")}
                />
            </div>

            {error ? (
                <Card className="mt-0 border-[#d50000]/40">
                    <CardContent className="flex flex-col items-start gap-3 p-6 text-sm">
                        <div>
                            <span className="font-semibold text-[#d50000]">Could not load account health</span>
                            {" - "}
                            {error}
                        </div>
                        <div className="flex items-center gap-3">
                            <Button type="button" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                                Retry
                            </Button>
                            {day && (
                                <Link to="/admin/account-health" className="text-xs text-primary underline">
                                    Show the latest night
                                </Link>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <AccountHealthList
                    rows={visible}
                    day={data?.day ?? null}
                    loading={loading}
                    expanded={expanded}
                    onToggle={toggle}
                    emptyMessage={emptyMessage}
                />
            )}
        </div>
    );
};

export default AccountHealth;
